import { TransactionCategory, TransactionFlow } from '../types/accounts.types';
import { Big, roundMoney } from './big-helpers';

/**
 * Transaction flow configuration - the single source of truth for transaction flow directions.
 *
 * All inflows are positive, all outflows are negative.
 * - INFLOW: Money coming into the account (positive amount)
 * - OUTFLOW: Money leaving the account (negative amount)
 * - SIGN_BASED: Flow is determined by the sign of the amount (positive = INFLOW, negative = OUTFLOW)
 */
export type TransactionFlowType = TransactionFlow | 'SIGN_BASED';

/**
 * Maps each transaction category to its expected flow direction.
 * This is the single source of truth for flow determination.
 */
export const TRANSACTION_FLOW_CONFIG: Record<
  TransactionCategory,
  TransactionFlowType
> = {
  // Sales transactions
  [TransactionCategory.SALES_RECEIPT]: TransactionFlow.INFLOW,
  [TransactionCategory.SALES_REFUND]: TransactionFlow.OUTFLOW,

  // Debt/Loan transactions
  [TransactionCategory.DEBT_PAYMENT]: TransactionFlow.INFLOW,
  [TransactionCategory.LOAN_PAYMENT]: TransactionFlow.INFLOW,
  [TransactionCategory.LOAN_GIVEN]: TransactionFlow.OUTFLOW,

  // Transfer transactions
  [TransactionCategory.TRANSFER_IN]: TransactionFlow.INFLOW,
  [TransactionCategory.TRANSFER_OUT]: TransactionFlow.OUTFLOW,

  // Investment/Dividend transactions
  [TransactionCategory.INVESTMENT]: TransactionFlow.INFLOW,
  [TransactionCategory.DIVIDEND]: TransactionFlow.OUTFLOW,

  // Supplier transactions
  [TransactionCategory.SUPPLIER_PAYMENT]: TransactionFlow.OUTFLOW,
  [TransactionCategory.SUPPLIER_REFUND]: TransactionFlow.INFLOW,

  // Expense transactions
  [TransactionCategory.EXPENSE]: TransactionFlow.OUTFLOW,

  // Correction transactions - flow is determined by the sign of the amount
  [TransactionCategory.CORRECTION]: 'SIGN_BASED',
  [TransactionCategory.OPENING_CORRECTION]: 'SIGN_BASED',
  [TransactionCategory.CLOSING_CORRECTION]: 'SIGN_BASED',

  // Reversal transactions (mirror flow of their originals)
  [TransactionCategory.DEBT_PAYMENT_REVERSAL]: TransactionFlow.OUTFLOW,
  [TransactionCategory.LOAN_PAYMENT_REVERSAL]: TransactionFlow.OUTFLOW,
  [TransactionCategory.LOAN_GIVEN_REVERSAL]: TransactionFlow.INFLOW,
  [TransactionCategory.EXPENSE_REVERSAL]: TransactionFlow.INFLOW,
  [TransactionCategory.DIVIDEND_REVERSAL]: TransactionFlow.INFLOW,
  [TransactionCategory.INVESTMENT_REVERSAL]: TransactionFlow.OUTFLOW,
};

/**
 * Gets the expected flow for a transaction category.
 * For SIGN_BASED categories, returns undefined - use getFlowFromAmount instead.
 */
export function getExpectedFlow(
  category: TransactionCategory,
): TransactionFlow | undefined {
  const flowConfig = TRANSACTION_FLOW_CONFIG[category];
  return flowConfig === 'SIGN_BASED' ? undefined : flowConfig;
}

/**
 * Gets the expected flow for a transaction category, throwing an error if not found.
 * Use this when you know the category has a fixed flow (not SIGN_BASED).
 */
export function getRequiredFlow(
  category: TransactionCategory,
): TransactionFlow {
  const flow = getExpectedFlow(category);
  if (flow === undefined) {
    throw new Error(
      `Category ${category} does not have a fixed flow. Use getFlowFromAmount for sign-based categories.`,
    );
  }
  return flow;
}

/**
 * Determines the flow direction based on the amount sign.
 * Used for SIGN_BASED transaction categories (corrections).
 *
 * @param amount - The transaction amount (can be positive or negative)
 * @returns INFLOW for positive/zero amounts, OUTFLOW for negative amounts
 */
export function getFlowFromAmount(amount: number): TransactionFlow {
  return amount >= 0 ? TransactionFlow.INFLOW : TransactionFlow.OUTFLOW;
}

/**
 * Determines the correct flow for a transaction based on category and amount.
 * Handles both fixed-flow and sign-based categories.
 *
 * @param category - The transaction category
 * @param amount - The transaction amount
 * @param providedFlow - Optional flow override (used for validation)
 * @returns The determined flow direction
 */
export function determineTransactionFlow(
  category: TransactionCategory,
  amount: number,
  providedFlow?: TransactionFlow,
): TransactionFlow {
  const flowConfig = TRANSACTION_FLOW_CONFIG[category];

  if (flowConfig === 'SIGN_BASED') {
    // For sign-based categories, use the provided flow if given, otherwise determine from amount
    return providedFlow ?? getFlowFromAmount(amount);
  }

  // For fixed-flow categories, always return the expected flow
  return flowConfig;
}

/**
 * Checks if a transaction category has a sign-based flow.
 */
export function isSignBasedCategory(category: TransactionCategory): boolean {
  return TRANSACTION_FLOW_CONFIG[category] === 'SIGN_BASED';
}

/**
 * Validates that the provided flow matches the expected flow for a category.
 * For sign-based categories, this always returns true (any flow is valid).
 *
 * @param category - The transaction category
 * @param flow - The flow to validate
 * @returns True if the flow is valid for the category
 */
export function isValidFlowForCategory(
  category: TransactionCategory,
  flow: TransactionFlow,
): boolean {
  const expectedFlow = getExpectedFlow(category);
  return expectedFlow === undefined || expectedFlow === flow;
}

/**
 * Normalizes a transaction amount based on flow direction.
 * Ensures INFLOW amounts are positive and OUTFLOW amounts are negative.
 *
 * @param amount - The input amount (can be positive or negative)
 * @param flow - The transaction flow direction
 * @returns Normalized amount (positive for INFLOW, negative for OUTFLOW)
 */
export function normalizeAmountByFlow(
  amount: number,
  flow: TransactionFlow,
): number {
  const absAmount = Math.abs(amount);
  return flow === TransactionFlow.INFLOW ? absAmount : -absAmount;
}

/**
 * Applies a normalized transaction amount to a balance.
 * Since transaction amounts are already normalized (positive for INFLOW, negative for OUTFLOW),
 * we simply add the transaction amount to the balance.
 *
 * @param currentBalance - The current balance amount
 * @param transactionAmount - The transaction amount (already normalized by flow)
 * @returns The new balance after applying the transaction
 */
export function applyTransactionToBalance(
  currentBalance: number,
  transactionAmount: number,
): number {
  // Since amounts are already normalized (positive for INFLOW, negative for OUTFLOW),
  // we can simply add the transaction amount to the balance.
  // Computed in Big and rounded to cents: this is the sanctioned way to
  // apply money to a balance, so it must never introduce float residue.
  return roundMoney(new Big(currentBalance).plus(transactionAmount));
}

/**
 * Alternative method to apply transaction to balance using flow direction.
 * This is useful when the amount is not pre-normalized.
 *
 * @param currentBalance - The current balance amount
 * @param amount - The absolute transaction amount
 * @param flow - The transaction flow direction
 * @returns The new balance after applying the transaction
 */
export function applyTransactionAmountToBalance(
  currentBalance: number,
  amount: number,
  flow: TransactionFlow,
): number {
  const normalizedAmount = normalizeAmountByFlow(amount, flow);
  return roundMoney(new Big(currentBalance).plus(normalizedAmount));
}

/**
 * Helper type for creating transactions with flow validation.
 */
export interface TransactionFlowInput {
  category: TransactionCategory;
  amount: number;
  flow?: TransactionFlow;
}

/**
 * Validates and returns the correct flow for a transaction input.
 * Throws an error if the provided flow conflicts with the expected flow.
 *
 * @param input - The transaction input with category, amount, and optional flow
 * @returns The validated flow direction
 * @throws Error if the provided flow is invalid for the category
 */
export function validateAndGetFlow(
  input: TransactionFlowInput,
): TransactionFlow {
  const { category, amount, flow: providedFlow } = input;
  const expectedFlow = getExpectedFlow(category);

  if (
    expectedFlow !== undefined &&
    providedFlow !== undefined &&
    providedFlow !== expectedFlow
  ) {
    throw new Error(
      `Invalid flow for category ${category}: expected ${expectedFlow}, got ${providedFlow}`,
    );
  }

  return determineTransactionFlow(category, amount, providedFlow);
}

/**
 * Creates a normalized transaction amount with validated flow.
 * This is the main entry point for creating transactions with proper flow handling.
 *
 * @param input - The transaction input with category, amount, and optional flow
 * @returns An object with the validated flow and normalized amount
 */
export function prepareTransactionAmount(input: TransactionFlowInput): {
  flow: TransactionFlow;
  normalizedAmount: number;
} {
  const flow = validateAndGetFlow(input);
  const normalizedAmount = normalizeAmountByFlow(input.amount, flow);

  return { flow, normalizedAmount };
}
